import { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { ZoomIn, ZoomOut, Maximize, GitBranch } from 'lucide-react';
import type { IterationGraphNode } from './api';
import { formatBeijingDate } from '../../../utils/time';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';
import { deriveThumbnailUrl } from '../../../utils/imageDisplay';

interface GraphNode extends d3.SimulationNodeDatum {
  id: string;
  data: IterationGraphNode;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

interface GraphLink extends d3.SimulationLinkDatum<GraphNode> {
  source: string | GraphNode;
  target: string | GraphNode;
}

interface IterationGraphViewProps {
  rootNodes: IterationGraphNode[];
  allNodes: Map<string, IterationGraphNode>;
  onNodeClick: (node: IterationGraphNode) => void;
  onContinueIteration?: (node: IterationGraphNode) => void;
  selectedNodeId?: string | null;
}

export function IterationGraphView({
  rootNodes,
  allNodes,
  onNodeClick,
  selectedNodeId,
}: IterationGraphViewProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const isDraggingRef = useRef(false);
  const dragStartTimeRef = useRef(0);

  // 构建图数据
  const buildGraphData = useCallback(() => {
    const nodes: GraphNode[] = [];
    const links: GraphLink[] = [];
    const processedIds = new Set<string>();

    const processNode = (node: IterationGraphNode) => {
      if (processedIds.has(node.id)) return;
      processedIds.add(node.id);

      nodes.push({
        id: node.id,
        data: node,
      });

      // 添加子节点连接
      node.childrenIds.forEach((childId: string) => {
        const child = allNodes.get(childId);
        if (child) {
          links.push({
            source: node.id,
            target: childId,
          });
          processNode(child);
        }
      });
    };

    rootNodes.forEach(processNode);
    return { nodes, links };
  }, [rootNodes, allNodes]);

  // 初始化 D3 力导向图
  useEffect(() => {
    if (!svgRef.current || rootNodes.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const container = containerRef.current;
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight || 600;

    const { nodes, links } = buildGraphData();

    // 创建缩放行为
    const zoomBehavior = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 3])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
        setZoom(event.transform.k);
      });

    svg.call(zoomBehavior);

    // 创建主容器
    const g = svg.append('g');

    // 创建箭头标记
    const defs = svg.append('defs');
    
    defs.append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 45)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#94a3b8');

    // 创建力导向模拟
    const simulation = d3.forceSimulation<GraphNode>(nodes)
      .force('link', d3.forceLink<GraphNode, GraphLink>(links).id((d) => d.id).distance(140))
      .force('charge', d3.forceManyBody().strength(-1000))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(70));

    // 绘制连线
    const link = g.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', '#cbd5e1')
      .attr('stroke-width', 2)
      .attr('marker-end', 'url(#arrowhead)');

    // 绘制节点组
    const nodeGroup = g.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'node-group')
      .style('cursor', 'pointer');

    // 拖拽行为
    const dragBehavior = d3.drag<SVGGElement, GraphNode>()
      .on('start', (event, d) => {
        dragStartTimeRef.current = Date.now();
        isDraggingRef.current = false;
        if (!event.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      })
      .on('drag', (event, d) => {
        isDraggingRef.current = true;
        d.fx = event.x;
        d.fy = event.y;
      })
      .on('end', (event, d) => {
        if (!event.active) simulation.alphaTarget(0);
        d.fx = null;
        d.fy = null;
        // 延迟重置拖拽状态，防止点击事件被误判
        setTimeout(() => {
          isDraggingRef.current = false;
        }, 50);
      });

    nodeGroup.call(dragBehavior);

    // 节点外圈（选中状态）
    nodeGroup.append('circle')
      .attr('class', 'selection-ring')
      .attr('r', 44)
      .attr('fill', 'none')
      .attr('stroke', (d) => d.data.id === selectedNodeId ? '#3b82f6' : 'transparent')
      .attr('stroke-width', 3);

    // 节点背景圆
    nodeGroup.append('circle')
      .attr('class', 'node-bg')
      .attr('r', 40)
      .attr('fill', (d) => d.data.isDeleted ? '#f1f5f9' : '#ffffff')
      .attr('stroke', (d) => {
        if (d.data.isDeleted) return '#94a3b8';
        if (d.data.status === 'failed') return '#ef4444';
        if (d.data.status === 'processing') return '#f59e0b';
        return '#e2e8f0';
      })
      .attr('stroke-width', 2)
      .style('filter', (d) => d.data.isDeleted ? 'grayscale(100%)' : 'none');

    // 图片裁剪路径
    defs.selectAll('clipPath')
      .data(nodes)
      .enter()
      .append('clipPath')
      .attr('id', (d) => `clip-${d.id}`)
      .append('circle')
      .attr('r', 36);

    // 节点图片或删除标记
    nodeGroup.each(function(d) {
      const group = d3.select(this);
      
      if (d.data.isDeleted) {
        // 已删除状态 - 显示删除图标
        group.append('circle')
          .attr('r', 36)
          .attr('fill', '#e2e8f0');
        
        // 删除图标背景
        group.append('circle')
          .attr('r', 20)
          .attr('fill', '#94a3b8');
        
        // 删除文字
        group.append('text')
          .attr('text-anchor', 'middle')
          .attr('dy', '0.35em')
          .attr('fill', '#ffffff')
          .attr('font-size', '10px')
          .attr('font-weight', 'bold')
          .text('已删');
      } else if (d.data.imageUrl) {
        // 正常图片：列表场景优先 384px 小缩略图，失败回落原图，再失败显示占位
        const originalUrl = resolveBrowserMediaUrl(d.data.imageUrl);
        const thumbUrl = deriveThumbnailUrl(originalUrl.split('?')[0], 'small');
        const primaryUrl = thumbUrl ? resolveBrowserMediaUrl(thumbUrl) : originalUrl;
        const showPlaceholder = (img: SVGImageElement) => {
          d3.select(img).style('display', 'none');
          group.append('rect')
            .attr('x', -36)
            .attr('y', -36)
            .attr('width', 72)
            .attr('height', 72)
            .attr('fill', '#f1f5f9')
            .attr('rx', 36);
          group.append('text')
            .attr('text-anchor', 'middle')
            .attr('dy', '0.35em')
            .attr('fill', '#94a3b8')
            .attr('font-size', '10px')
            .text('无图片');
        };
        group.append('image')
          .attr('xlink:href', primaryUrl)
          .attr('x', -36)
          .attr('y', -36)
          .attr('width', 72)
          .attr('height', 72)
          .attr('clip-path', `url(#clip-${d.id})`)
          .on('error', function (this: SVGImageElement) {
            const current = this.getAttribute('href') || this.getAttribute('xlink:href') || '';
            if (current === primaryUrl && primaryUrl !== originalUrl) {
              d3.select(this).attr('xlink:href', originalUrl);
              return;
            }
            showPlaceholder(this);
          });
      } else {
        // 无图片状态
        group.append('circle')
          .attr('r', 36)
          .attr('fill', '#f1f5f9');
        
        const iconGroup = group.append('g')
          .attr('class', 'status-icon');
        
        if (d.data.status === 'processing') {
          iconGroup.append('circle')
            .attr('r', 18)
            .attr('fill', '#fef3c7');
        } else if (d.data.status === 'failed') {
          iconGroup.append('circle')
            .attr('r', 18)
            .attr('fill', '#fee2e2');
        } else {
          iconGroup.append('circle')
            .attr('r', 18)
            .attr('fill', '#e0e7ff');
        }
      }
    });

    // 版本标签背景
    nodeGroup.append('circle')
      .attr('cx', 30)
      .attr('cy', -30)
      .attr('r', 13)
      .attr('fill', (d) => d.data.isDeleted ? '#94a3b8' : '#3b82f6');

    // 版本标签文字
    nodeGroup.append('text')
      .attr('x', 30)
      .attr('y', -30)
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('fill', '#ffffff')
      .attr('font-size', '10px')
      .attr('font-weight', 'bold')
      .text((d) => `V${d.data.generation}`);

    // 状态指示器
    nodeGroup.each(function(d) {
      const group = d3.select(this);
      
      if (d.data.isDeleted) {
        // 已删除状态指示器
        group.append('circle')
          .attr('cx', 30)
          .attr('cy', 30)
          .attr('r', 9)
          .attr('fill', '#64748b')
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 2);
      } else {
        const statusColor = d.data.status === 'completed' ? '#22c55e' : 
                           d.data.status === 'failed' ? '#ef4444' : '#f59e0b';
        
        group.append('circle')
          .attr('cx', 30)
          .attr('cy', 30)
          .attr('r', 9)
          .attr('fill', statusColor)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 2);
      }
    });

    // 提示词标签（悬停显示）
    const tooltipGroup = nodeGroup.append('g')
      .attr('class', 'tooltip')
      .style('opacity', 0)
      .style('pointer-events', 'none');

    tooltipGroup.append('rect')
      .attr('x', -90)
      .attr('y', 55)
      .attr('width', 180)
      .attr('height', (d) => d.data.isDeleted ? 55 : 45)
      .attr('rx', 8)
      .attr('fill', 'rgba(0, 0, 0, 0.85)');

    tooltipGroup.append('text')
      .attr('x', 0)
      .attr('y', 75)
      .attr('text-anchor', 'middle')
      .attr('fill', '#ffffff')
      .attr('font-size', '11px')
      .text((d) => {
        const prompt = d.data.prompt || '无提示词';
        return prompt.length > 24 ? prompt.slice(0, 24) + '...' : prompt;
      });

    // 已删除标记
    tooltipGroup.filter((d) => !!d.data.isDeleted)
      .append('text')
      .attr('x', 0)
      .attr('y', 95)
      .attr('text-anchor', 'middle')
      .attr('fill', '#f87171')
      .attr('font-size', '10px')
      .attr('font-weight', 'bold')
      .text('【该图片已被删除】');

    tooltipGroup.filter((d) => !d.data.isDeleted)
      .append('text')
      .attr('x', 0)
      .attr('y', 92)
      .attr('text-anchor', 'middle')
      .attr('fill', '#94a3b8')
      .attr('font-size', '9px')
      .text((d) => formatBeijingDate(d.data.createdAt));

    // 鼠标事件
    nodeGroup
      .on('mouseenter', function() {
        d3.select(this).select('.tooltip').style('opacity', 1);
        d3.select(this).select('.node-bg')
          .attr('stroke', '#3b82f6')
          .attr('stroke-width', 3);
      })
      .on('mouseleave', function(_event, d) {
        d3.select(this).select('.tooltip').style('opacity', 0);
        d3.select(this).select('.node-bg')
          .attr('stroke', d.data.isDeleted ? '#94a3b8' :
                        d.data.status === 'failed' ? '#ef4444' : 
                        d.data.status === 'processing' ? '#f59e0b' : '#e2e8f0')
          .attr('stroke-width', 2);
      })
      .on('click', (_event, d) => {
        // 只有非拖拽状态才触发点击
        const dragDuration = Date.now() - dragStartTimeRef.current;
        if (!isDraggingRef.current && dragDuration < 300) {
          onNodeClick(d.data);
        }
      });

    // 更新位置
    simulation.on('tick', () => {
      link
        .attr('x1', (d) => (d.source as GraphNode).x!)
        .attr('y1', (d) => (d.source as GraphNode).y!)
        .attr('x2', (d) => (d.target as GraphNode).x!)
        .attr('y2', (d) => (d.target as GraphNode).y!);

      nodeGroup
        .attr('transform', (d) => `translate(${d.x},${d.y})`);
    });

    // 清理函数
    return () => {
      simulation.stop();
    };
  }, [rootNodes, allNodes, selectedNodeId, onNodeClick, buildGraphData]);

  // 缩放控制
  const handleZoomIn = () => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(
      (d3.zoom() as any).transform,
      d3.zoomTransform(svgRef.current).scale(zoom * 1.2)
    );
  };

  const handleZoomOut = () => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(
      (d3.zoom() as any).transform,
      d3.zoomTransform(svgRef.current).scale(zoom * 0.8)
    );
  };

  const handleReset = () => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(500).call(
      (d3.zoom() as any).transform,
      d3.zoomIdentity
    );
  };

  if (rootNodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 text-light-muted">
        <div className="text-center">
          <GitBranch className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>暂无迭代数据</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full h-[600px] bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl border border-light-border overflow-hidden">
      {/* 控制按钮 */}
      <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
        <button
          onClick={handleZoomIn}
          className="p-2 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow text-light-text hover:text-accent-blue"
          title="放大"
        >
          <ZoomIn className="w-5 h-5" />
        </button>
        <button
          onClick={handleZoomOut}
          className="p-2 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow text-light-text hover:text-accent-blue"
          title="缩小"
        >
          <ZoomOut className="w-5 h-5" />
        </button>
        <button
          onClick={handleReset}
          className="p-2 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow text-light-text hover:text-accent-blue"
          title="重置视图"
        >
          <Maximize className="w-5 h-5" />
        </button>
      </div>

      {/* 图例 */}
      <div className="absolute bottom-4 left-4 z-10 bg-white/90 backdrop-blur rounded-xl p-3 shadow-md">
        <h4 className="text-xs font-medium text-light-text mb-2">图例</h4>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-xs text-light-muted">已完成</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500" />
            <span className="text-xs text-light-muted">生成中</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="text-xs text-light-muted">失败</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-slate-400" />
            <span className="text-xs text-light-muted">已删除</span>
          </div>
        </div>
      </div>

      {/* 提示信息 */}
      <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur rounded-xl p-3 shadow-md max-w-xs">
        <p className="text-xs text-light-muted">
          <span className="font-medium text-light-text">操作提示：</span>
          拖拽节点可调整布局，滚轮缩放，点击节点查看详情
        </p>
      </div>

      {/* SVG 画布 */}
      <svg
        ref={svgRef}
        className="w-full h-full"
        style={{ cursor: isDraggingRef.current ? 'grabbing' : 'grab' }}
      />
    </div>
  );
}

export default IterationGraphView;
